import { useEffect, useRef, useState } from "react";

const INK = "#2b1d12";
const INK_SOFT = "#4a3a2a";
const GOLD = "#8a6a3b";
const PARCHMENT = "#faf5ec";
const RULE = "#e3d6bf";

const STORAGE_KEY = "queen-farah:oath-sworn";
const OATH = "I LOVE THE QUEEN";
const APPEAR_MIN_MS = 45_000;
const APPEAR_MAX_MS = 90_000;

export function RoyalOath() {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (typeof localStorage !== "undefined" && localStorage.getItem(STORAGE_KEY)) {
      return;
    }
    const delay = APPEAR_MIN_MS + Math.random() * (APPEAR_MAX_MS - APPEAR_MIN_MS);
    const t = setTimeout(() => setOpen(true), delay);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    inputRef.current?.focus();
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!open) return null;

  const matches = value === OATH;

  const onSubmit = () => {
    if (!matches) return;
    try {
      localStorage.setItem(STORAGE_KEY, "1");
    } catch {}
    setOpen(false);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-5 font-serif"
      style={{ backgroundColor: "rgba(43, 29, 18, 0.55)" }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="oath-title"
    >
      <div
        className="w-full max-w-md border p-8 sm:p-10 shadow-2xl"
        style={{ backgroundColor: PARCHMENT, borderColor: RULE, color: INK }}
      >
        <div
          className="text-[10px] sm:text-xs tracking-[0.3em] uppercase text-center mb-4"
          style={{ color: GOLD }}
        >
          A Royal Interruption
        </div>
        <h2
          id="oath-title"
          className="text-2xl sm:text-3xl italic leading-tight text-center text-pretty"
          style={{ color: INK }}
        >
          Halt, subject!
        </h2>
        <div className="mx-auto w-12 h-px my-6" style={{ backgroundColor: GOLD }} />
        <p
          className="text-base leading-relaxed italic text-center text-pretty mb-8"
          style={{ color: INK_SOFT }}
        >
          You shall not continue until you have sworn the oath, in the proper
          tongue of the realm, with all due reverence. That is to say, in
          capital letters.
        </p>
        <div
          className="text-center text-[11px] tracking-[0.3em] uppercase mb-3"
          style={{ color: GOLD }}
        >
          Recite the following
        </div>
        <div
          className="text-center tracking-[0.2em] mb-6 select-none"
          style={{ color: INK, fontWeight: 500 }}
        >
          I LOVE THE QUEEN
        </div>
        <input
          ref={inputRef}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && matches) onSubmit();
            if (e.key === "Escape") e.preventDefault();
          }}
          autoCapitalize="characters"
          autoCorrect="off"
          spellCheck={false}
          className="w-full px-4 py-3 text-center tracking-[0.15em] border bg-transparent focus:outline-none focus:border-current"
          style={{ borderColor: RULE, color: INK, fontFamily: "inherit" }}
          placeholder="type the oath here..."
          aria-label="Royal oath"
        />
        <button
          type="button"
          disabled={!matches}
          onClick={onSubmit}
          className="mt-6 w-full py-3 text-[11px] tracking-[0.3em] uppercase border transition-colors"
          style={{
            borderColor: matches ? INK : RULE,
            color: matches ? PARCHMENT : GOLD,
            backgroundColor: matches ? INK : "transparent",
            cursor: matches ? "pointer" : "not-allowed",
            opacity: matches ? 1 : 0.7,
          }}
        >
          {matches ? "Long may she reign" : "Awaiting the oath"}
        </button>
      </div>
    </div>
  );
}
